import type { GridApi, IDatasource, IGetRowsParams, IRowNode } from "ag-grid-community";
import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import type { TraceGridRow } from "./types";

import { buildTracesSearchParams } from "@/components/TracesSearch/buildTracesSearchParams";
import { useStructuredTracesRefresh } from "@/components/TracesSearch/hooks/useStructuredTracesRefresh";
import { useStructuredSearchStore } from "@/components/StructuredSearch";
import { useUserToken } from "@/context/auth-context";
import { useUser } from "@/context/user-context";
import {
  applyTraceEntityHydrationToItems,
  collectTraceEntityHydrationIds,
  fetchTraceEntityHydration,
  fetchTracesSearchPaginated,
  getTraceEntityHydrationQueryKey,
  getTraceEntityHydrationQueryKeyPrefix,
  getTracesSearchPaginatedQueryKey,
  getTracesSearchPaginatedQueryKeyPrefix,
  hasTraceEntityHydrationIds,
  type TracesSearchParams,
  type WorkflowInputVariableScope,
} from "@/queries/traces";
import type { TraceListItem } from "@/types/traces";
import { displayErrorToast } from "@/utils/toast";
const TRACES_QUERY_CACHE_MS = 5 * 60 * 1000;
const TRACE_ENTITY_HYDRATION_CACHE_MS = 5 * 60 * 1000;
const CURSOR_CHAIN_START_ROW = 0;

const mergeTraceRowHydration = (row: TraceGridRow, item: TraceListItem): TraceGridRow => ({
  ...row,
  hydrated_prompt_names: item.hydrated_prompt_names,
  hydrated_workflow_names: item.hydrated_workflow_names,
  entity_hydration_status: item.entity_hydration_status,
  input_variables: item.input_variables,
});

const isQueryCancellationError = (error: unknown): boolean => {
  if (!(error instanceof Error)) {
    return false;
  }
  return error.name === "AbortError" || error.name === "CancelledError";
};

const serializeSearchParamsWithoutSort = (params: TracesSearchParams): string =>
  JSON.stringify({ filterGroup: params.filterGroup ?? null });

export const useTracesGridData = (workflowInputVariableScope?: WorkflowInputVariableScope) => {
  const structuredSearchStore = useStructuredSearchStore();
  const queryClient = useQueryClient();
  const { clearNewSpansBadge, advanceStructuredEndIfNeeded } = useStructuredTracesRefresh();
  const workspaceId = useUser()?.activeWorkspaceId ?? 0;
  const userToken = useUserToken();

  const gridApiRef = useRef<GridApi<TraceGridRow> | null>(null);
  const hasCompletedInitialLoadRef = useRef(false);
  const latestGetRowsRequestIdRef = useRef(0);
  const [hasCompletedInitialLoad, setHasCompletedInitialLoad] = useState(false);
  const [isTableLoading, setIsTableLoading] = useState(false);
  const [lastRowCount, setLastRowCount] = useState<number | null>(null);

  const cursorByStartRowRef = useRef<Map<number, Record<string, unknown> | null>>(
    new Map([[CURSOR_CHAIN_START_ROW, null]]),
  );

  const searchParams: TracesSearchParams = buildTracesSearchParams(structuredSearchStore);
  const searchParamsKey = JSON.stringify(searchParams);
  const searchParamsWithoutSortKey = serializeSearchParamsWithoutSort(searchParams);

  const searchParamsRef = useRef(searchParams);
  searchParamsRef.current = searchParams;
  const searchParamsWithoutSortKeyRef = useRef(searchParamsWithoutSortKey);
  const pageIndexRef = useRef(structuredSearchStore.pageIndex);
  pageIndexRef.current = structuredSearchStore.pageIndex;

  const restoreGridPageIfNeeded = useCallback((api: GridApi<TraceGridRow>) => {
    queueMicrotask(() => {
      const pageIndex = pageIndexRef.current;
      if (pageIndex > 1) {
        api.paginationGoToPage(pageIndex - 1);
      }
    });
  }, []);

  const datasource = useMemo<IDatasource | undefined>(() => {
    if (!userToken || !workspaceId || !structuredSearchStore.urlHydrationReady) {
      return undefined;
    }

    return {
      rowCount: undefined,
      getRows: async (params: IGetRowsParams) => {
        const pageSize = Math.max(
          structuredSearchStore.pageSize,
          params.endRow - params.startRow || structuredSearchStore.pageSize,
        );
        const page = Math.floor(params.startRow / pageSize) + 1;
        const latestSearchParams = searchParamsRef.current;
        const requestId = latestGetRowsRequestIdRef.current + 1;
        latestGetRowsRequestIdRef.current = requestId;
        const fetchPage = (pageNumber: number, afterKey: Record<string, unknown> | null) =>
          queryClient.fetchQuery(
            getTracesSearchPaginatedQueryKey({
              userToken,
              workspaceId,
              page: pageNumber,
              pageSize,
              searchParams: latestSearchParams,
              afterKey,
            }),
            ({ signal }) =>
              fetchTracesSearchPaginated({
                signal,
                userToken,
                workspaceId,
                page: pageNumber,
                pageSize,
                searchParams: latestSearchParams,
                afterKey,
              }),
            { staleTime: 0, cacheTime: TRACES_QUERY_CACHE_MS },
          );
        const hydrateRowsForPage = async (items: TraceListItem[]) => {
          const ids = collectTraceEntityHydrationIds(items, {
            includeInputVariables: true,
          });
          if (!hasTraceEntityHydrationIds(ids)) {
            return;
          }

          try {
            const hydration = await queryClient.fetchQuery(
              getTraceEntityHydrationQueryKey({
                workspaceId,
                promptIds: ids.promptIds,
                workflowIds: ids.workflowIds,
                traceIds: ids.traceIds,
                workflowInputVariableScope,
              }),
              ({ signal }) =>
                fetchTraceEntityHydration({
                  signal,
                  userToken,
                  workspaceId,
                  promptIds: ids.promptIds,
                  workflowIds: ids.workflowIds,
                  traceIds: ids.traceIds,
                  workflowInputVariableScope,
                }),
              {
                staleTime: TRACE_ENTITY_HYDRATION_CACHE_MS,
                cacheTime: TRACE_ENTITY_HYDRATION_CACHE_MS,
              },
            );
            if (requestId !== latestGetRowsRequestIdRef.current) {
              return;
            }
            const hydratedItems = applyTraceEntityHydrationToItems({
              items,
              hydration,
              status: "complete",
              includeInputVariables: true,
            });
            const api = gridApiRef.current;
            if (!api) {
              return;
            }

            const rowNodes: IRowNode<TraceGridRow>[] = [];
            hydratedItems.forEach(item => {
              const rowNode = api.getRowNode(item.trace_id);
              if (!rowNode?.data) {
                return;
              }
              rowNode.setData(mergeTraceRowHydration(rowNode.data, item));
              rowNodes.push(rowNode);
            });

            if (rowNodes.length > 0) {
              api.refreshCells({ rowNodes, force: true });
            }
          } catch (error) {
            if (isQueryCancellationError(error)) {
              return;
            }
            if (requestId !== latestGetRowsRequestIdRef.current) {
              return;
            }
            const api = gridApiRef.current;
            if (!api) {
              return;
            }

            const fallbackItems = applyTraceEntityHydrationToItems({
              items,
              status: "error",
              includeInputVariables: true,
            });
            const rowNodes: IRowNode<TraceGridRow>[] = [];
            fallbackItems.forEach(item => {
              const rowNode = api.getRowNode(item.trace_id);
              if (!rowNode?.data) {
                return;
              }
              rowNode.setData(mergeTraceRowHydration(rowNode.data, item));
              rowNodes.push(rowNode);
            });

            if (rowNodes.length > 0) {
              api.refreshCells({ rowNodes, force: true });
            }
          }
        };
        const resolveAfterKeyForStartRow = async (): Promise<{
          afterKey: Record<string, unknown> | null;
          lastRow?: number;
          usePagePagination?: boolean;
        }> => {
          if (params.startRow === CURSOR_CHAIN_START_ROW) {
            return { afterKey: null };
          }

          const cachedAfterKey = cursorByStartRowRef.current.get(params.startRow);
          if (cachedAfterKey !== undefined) {
            return { afterKey: cachedAfterKey };
          }

          let cursor = cursorByStartRowRef.current.get(CURSOR_CHAIN_START_ROW) ?? null;
          for (
            let startRow = CURSOR_CHAIN_START_ROW;
            startRow < params.startRow;
            startRow += pageSize
          ) {
            const nextStartRow = startRow + pageSize;
            const knownNextCursor = cursorByStartRowRef.current.get(nextStartRow);
            if (knownNextCursor !== undefined) {
              cursor = knownNextCursor;
              continue;
            }

            const cursorPage = Math.floor(startRow / pageSize) + 1;
            const response = await fetchPage(cursorPage, cursor);
            if (response.pagination_mode !== "cursor") {
              return { afterKey: null, usePagePagination: true };
            }

            if (!response.next_after_key) {
              const lastRow = startRow + response.items.length;
              cursorByStartRowRef.current.set(nextStartRow, null);
              return { afterKey: null, lastRow };
            }

            cursorByStartRowRef.current.set(nextStartRow, response.next_after_key);
            cursor = response.next_after_key;
          }

          return { afterKey: cursorByStartRowRef.current.get(params.startRow) ?? null };
        };

        setIsTableLoading(true);
        try {
          const cursorResolution = await resolveAfterKeyForStartRow();
          if (requestId !== latestGetRowsRequestIdRef.current) {
            params.failCallback();
            return;
          }
          if (cursorResolution.lastRow !== undefined) {
            params.successCallback([], cursorResolution.lastRow);
            setLastRowCount(cursorResolution.lastRow);
            if (!hasCompletedInitialLoadRef.current) {
              hasCompletedInitialLoadRef.current = true;
              setHasCompletedInitialLoad(true);
            }
            return;
          }

          const response = await fetchPage(
            page,
            cursorResolution.usePagePagination ? null : cursorResolution.afterKey,
          );
          if (requestId !== latestGetRowsRequestIdRef.current) {
            params.failCallback();
            return;
          }

          let lastRow: number | undefined;
          if (response.pagination_mode === "cursor") {
            if (response.next_after_key) {
              cursorByStartRowRef.current.set(params.endRow, response.next_after_key);
              lastRow = undefined;
            } else {
              lastRow = params.startRow + response.items.length;
            }
            if (params.startRow === 0 && response.items.length === 0) {
              setLastRowCount(0);
            } else {
              setLastRowCount(null);
            }
          } else {
            lastRow =
              response.items.length < pageSize
                ? params.startRow + response.items.length
                : response.total;
            setLastRowCount(response.total);
          }

          const visibleItems = applyTraceEntityHydrationToItems({
            items: response.items,
            status: "pending",
            includeInputVariables: true,
          });

          params.successCallback(visibleItems, lastRow);
          void hydrateRowsForPage(response.items);

          if (!hasCompletedInitialLoadRef.current) {
            hasCompletedInitialLoadRef.current = true;
            setHasCompletedInitialLoad(true);
          }
        } catch (error) {
          if (isQueryCancellationError(error)) {
            params.failCallback();
            return;
          }
          if (requestId !== latestGetRowsRequestIdRef.current) {
            params.failCallback();
            return;
          }
          setLastRowCount(null);
          params.failCallback();
          displayErrorToast("Error loading traces");
        } finally {
          if (requestId === latestGetRowsRequestIdRef.current) {
            setIsTableLoading(false);
          }
        }
      },
    };
  }, [
    queryClient,
    structuredSearchStore.pageSize,
    structuredSearchStore.urlHydrationReady,
    userToken,
    workspaceId,
    workflowInputVariableScope,
  ]);

  const isInitialSearchParams = useRef(true);
  useEffect(() => {
    if (isInitialSearchParams.current) {
      isInitialSearchParams.current = false;
      searchParamsWithoutSortKeyRef.current = searchParamsWithoutSortKey;
      return;
    }
    const isSortOnlyChange = searchParamsWithoutSortKeyRef.current === searchParamsWithoutSortKey;
    searchParamsWithoutSortKeyRef.current = searchParamsWithoutSortKey;
    setLastRowCount(null);
    cursorByStartRowRef.current = new Map([[CURSOR_CHAIN_START_ROW, null]]);
    if (isSortOnlyChange) {
      return;
    }

    latestGetRowsRequestIdRef.current += 1;
    void queryClient.cancelQueries({
      queryKey: getTracesSearchPaginatedQueryKeyPrefix(userToken, workspaceId),
      exact: false,
    });
    void queryClient.cancelQueries({
      queryKey: getTraceEntityHydrationQueryKeyPrefix(workspaceId),
      exact: false,
    });
    gridApiRef.current?.refreshInfiniteCache();
  }, [queryClient, searchParamsKey, searchParamsWithoutSortKey, userToken, workspaceId]);
  useEffect(() => {
    if (!structuredSearchStore.urlHydrationReady) {
      return;
    }
    cursorByStartRowRef.current = new Map([[CURSOR_CHAIN_START_ROW, null]]);
    const api = gridApiRef.current;
    if (!api) {
      return;
    }
    restoreGridPageIfNeeded(api);
  }, [restoreGridPageIfNeeded, structuredSearchStore.urlHydrationReady]);

  const setGridApiRef = useCallback(
    (api: GridApi<TraceGridRow>) => {
      gridApiRef.current = api;

      if (!structuredSearchStore.urlHydrationReady) {
        return;
      }

      restoreGridPageIfNeeded(api);
    },
    [restoreGridPageIfNeeded, structuredSearchStore.urlHydrationReady],
  );

  const handleManualRefresh = useCallback(() => {
    try {
      clearNewSpansBadge();
      setLastRowCount(null);
      cursorByStartRowRef.current = new Map([[CURSOR_CHAIN_START_ROW, null]]);

      const shouldAdvanceEnd = advanceStructuredEndIfNeeded();

      latestGetRowsRequestIdRef.current += 1;
      void queryClient.cancelQueries({
        queryKey: getTracesSearchPaginatedQueryKeyPrefix(userToken, workspaceId),
        exact: false,
      });
      void queryClient.cancelQueries({
        queryKey: getTraceEntityHydrationQueryKeyPrefix(workspaceId),
        exact: false,
      });
      void queryClient.removeQueries({
        queryKey: getTracesSearchPaginatedQueryKeyPrefix(userToken, workspaceId),
        exact: false,
      });
      void queryClient.removeQueries({
        queryKey: getTraceEntityHydrationQueryKeyPrefix(workspaceId),
        exact: false,
      });
      structuredSearchStore.resetToFirstPage();
      queueMicrotask(() => {
        const api = gridApiRef.current;
        if (!api) {
          return;
        }
        api.paginationGoToFirstPage();
        if (!shouldAdvanceEnd) {
          api.refreshInfiniteCache();
        }
      });
    } catch {
      displayErrorToast("Error refreshing traces");
    }
  }, [
    advanceStructuredEndIfNeeded,
    clearNewSpansBadge,
    queryClient,
    structuredSearchStore,
    userToken,
    workspaceId,
  ]);

  return {
    datasource,
    setGridApiRef,
    hasCompletedInitialLoad,
    handleManualRefresh,
    isTableLoading,
    totalRows: lastRowCount,
  };
};
